import React, { useState, useContext } from 'react';
import { Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import ConfirmModal from './ConfirmModal';
import NumberStepper from './NumberStepper';
import defaultValues from '../config/defaultValues.json';
import api from '../utils/Api';
import './PlayerSettingsModal.scss';
import { authContext } from '../contexts/AuthContext';

const PlayerSettingsModal = (props) => {
  const { player, className, isNew, isOpen, onToggleModal } = props;

  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [playerSettingsModal, setPlayerSettingsModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [confirmResetModal, setConfirmResetModal] = useState(false);
  const [playerName, setPlayerName] = useState(player ? player.name : defaultValues.player.name);
  const [tickerLineOne, setTickerLineOne] = useState(
    player ? player.tickerLineOne : defaultValues.player.tickerLineOne,
  );
  const [tickerLineTwo, setTickerLineTwo] = useState(
    player ? player.tickerLineTwo : defaultValues.player.tickerLineTwo,
  );
  const [tickerLineThree, setTickerLineThree] = useState(
    player ? player.tickerLineThree : defaultValues.player.tickerLineThree,
  );
  const [isHiddenAdArea, setIsHiddenAdArea] = useState(
    player ? player.isHiddenAdArea : defaultValues.player.isHiddenAdArea,
  );
  const [isHiddenTickerArea, setIsHiddenTickerArea] = useState(
    player ? player.isHiddenTickerArea : defaultValues.player.isHiddenTickerArea,
  );
  const [queueAddLimit, setQueueAddLimit] = useState(
    player ? player.queueAddLimit : defaultValues.player.queueAddLimit,
  );

  const context = useContext(authContext);

  const togglePlayerSettingsModal = () => {
    const currentModalState = playerSettingsModal;
    setPlayerSettingsModal(!currentModalState);
  };

  const toggleConfirmDeleteModal = () => {
    const currentModalState = confirmDeleteModal;
    setConfirmDeleteModal(!currentModalState);
  };

  const toggleConfirmResetModal = () => {
    const currentModalState = confirmResetModal;
    setConfirmResetModal(!currentModalState);
  };

  const handleDeletePlayerButtonClicked = (e: any) => {
    const token = context.auth.token;
    api
      .players(`${player.id}`, 'DELETE', token, null)
      .then((response) => {
        toggleConfirmDeleteModal();
        onToggleModal();
        console.log('Successfully deleted player: ', response);
      })
      .catch((error) => {
        showError(error.message);
        console.log('There has been a problem with your delete player operation: ' + error.message);
      });
  };

  const handleResetPlayerButtonClicked = (e: any) => {
    const token = context.auth.token;
    api
      .players(`reset/${player.id}`, 'POST', token, null)
      .then((response) => {
        toggleConfirmResetModal();
        onToggleModal();
        console.log('Successfully reset player: ', response);
      })
      .catch((error) => {
        showError(error.message);
        console.log('There has been a problem with your reset player operation: ' + error.message);
      });
  };

  const handleUpdatePlayerButtonClicked = (e: any) => {
    console.log('handleUpdatePlayerButtonClicked');

    if (!playerName) {
      showError('Please give your jukebox a name.');
      return;
    }

    let obj = {
      name: playerName,
      tickerLineOne: tickerLineOne,
      tickerLineTwo: tickerLineTwo,
      tickerLineThree: tickerLineThree,
      isHiddenAdArea: isHiddenAdArea,
      isHiddenTickerArea: isHiddenTickerArea,
      queueAddLimit: queueAddLimit,
    };

    const token = context.auth.token;
    if (!player) {
      api
        .players('create', 'POST', token, obj)
        .then((response) => {
          onToggleModal();
        })
        .catch((error) => {
          showError(error.message);
          console.log('There has been a problem with your create player operation: ' + error.message);
        });
    } else {
      api
        .players(`${player.id}`, 'PATCH', token, obj)
        .then((response) => {
          onToggleModal();
        })
        .catch((error) => {
          showError(error.message);
          console.log('There has been a problem with your create player operation: ' + error.message);
        });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
  };

  const handlePlayerNameChange = (e: any) => {
    setPlayerName(e.target.value);
  };

  const handleTickerLineOneChange = (e: any) => {
    setTickerLineOne(e.target.value);
  };

  const handleTickerLineTwoChange = (e: any) => {
    setTickerLineTwo(e.target.value);
  };

  const handleTickerLineThreeChange = (e: any) => {
    setTickerLineThree(e.target.value);
  };

  const handleIsHiddenAdAreaChange = (e: any) => {
    setIsHiddenAdArea(e.target.checked);
  };

  const handleIsHiddenTickerAreaChange = (e: any) => {
    setIsHiddenTickerArea(e.target.checked);
  };

  const handleQueueAddLimitChange = (num: number | null) => {
    let addLimit = 0;
    if (num) {
      addLimit = num;
    }
    setQueueAddLimit(addLimit);
  };

  const handleQueueAddLimitOnKeyPress = (e: any) => {
    e.preventDefault();
  };

  const showError = (errorText: string) => {
    setErrorText(errorText);
    setTimeout(hideError, 3000);
  };

  const hideError = () => {
    setErrorText('');
  };

  const showSuccess = (successText: string) => {
    setSuccessText(successText);
    setTimeout(hideSuccess, 3000);
  };

  const hideSuccess = () => {
    setSuccessText('');
  };

  let key = 'new';
  let buttonTitle = 'Create';
  let modalTitle = 'New jukebox';
  let deletePrompt = '';
  let resetPrompt = '';

  if (player) {
    key = player.id;
    buttonTitle = 'Save';
    modalTitle = 'Jukebox settings';
    deletePrompt = 'Are you sure you want to delete "' + player.name + ' - ' + player.playerIdentifier + '"?';
    resetPrompt = `Resetting this player will unpause, unmute and set the volume level at 50%. If a corruption is detected in the video queue then a repair will be attempted. If unable to repair the queue, this operation will clear out the current video queue.\n\nAre you sure you want to reset ${player.name} - ${player.playerIdentifier}?`;
  }

  return (
    <div key={key}>
      {confirmDeleteModal && (
        <ConfirmModal
          isOpen={confirmDeleteModal}
          prompt={deletePrompt}
          confirmTitle={'Delete'}
          onCancelClick={toggleConfirmDeleteModal}
          onConfirmClick={handleDeletePlayerButtonClicked}
        />
      )}
      {confirmResetModal && (
        <ConfirmModal
          isOpen={confirmResetModal}
          prompt={resetPrompt}
          confirmTitle={'Reset'}
          onCancelClick={toggleConfirmResetModal}
          onConfirmClick={handleResetPlayerButtonClicked}
        />
      )}
      <Modal isOpen={isOpen} toggle={onToggleModal} className={props.className} backdrop={'static'} keyboard={false}>
        <ModalHeader toggle={onToggleModal}>{modalTitle}</ModalHeader>
        <ModalBody>
          <Container>
            <div>
              {errorText && <Alert color="danger">{errorText}</Alert>}
              {successText && <Alert color="success">{successText}</Alert>}
            </div>
            <form className="playerSettings-form" onSubmit={handleSubmit}>
              <h4>
                <b>Ticker</b>
              </h4>
              <label>
                <span>Line 1:</span>
                <input
                  type="text"
                  maxLength={defaultValues.player.tickerCharLimit}
                  value={tickerLineOne}
                  onChange={handleTickerLineOneChange}
                />
              </label>
              <label>
                <span>Line 2:</span>
                <input
                  type="text"
                  maxLength={defaultValues.player.tickerCharLimit}
                  value={tickerLineTwo}
                  onChange={handleTickerLineTwoChange}
                />
              </label>
              <label>
                <span>Line 3:</span>
                <input
                  type="text"
                  maxLength={defaultValues.player.tickerCharLimit}
                  value={tickerLineThree}
                  onChange={handleTickerLineThreeChange}
                />
              </label>
              <label>
                <span>Jukebox name:</span>
                <input
                  type="text"
                  maxLength={defaultValues.player.nameCharLimit}
                  value={playerName}
                  onChange={handlePlayerNameChange}
                />
              </label>
              <br />
              <label>
                <span>Songs per person:</span>
                <NumberStepper
                  min={0}
                  value={queueAddLimit}
                  aria-label="Add to Queue Limit"
                  onChange={handleQueueAddLimitChange}
                  onKeyPress={handleQueueAddLimitOnKeyPress}
                />
              </label>
              {/*
                <br />
                <label>
                  <span>Hide Ad Area:</span>
                  <input
                    name="isHiddenAdArea"
                    type="checkbox"
                    defaultChecked={isHiddenAdArea}
                    onChange={this.handleIsHiddenAdAreaChange} />
                </label>
                <br />
                <label>
                  <span>Hide Ticker Area:</span>
                  <input
                    name="isHiddenTickerArea"
                    type="checkbox"
                    defaultChecked={isHiddenTickerArea}
                    onChange={this.handleIsHiddenTickerAreaChange} />
                </label>
                */}
            </form>
          </Container>
        </ModalBody>
        <ModalFooter>
          {player && (
            <div className="me-auto">
              <Button color="danger" onClick={toggleConfirmDeleteModal}>
                {'Delete'}
              </Button>
              <Button outline color="primary" className="ms-2" onClick={toggleConfirmResetModal}>
                {'Reset'}
              </Button>
            </div>
          )}
          <Button color="secondary" onClick={onToggleModal}>
            {'Cancel'}
          </Button>
          <Button color="primary" onClick={handleUpdatePlayerButtonClicked}>
            {buttonTitle}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PlayerSettingsModal;
